<template>
  <div
    ref="confettiAnchor"
    class="min-h-screen w-screen flex flex-col px-5 pb-5 lg:px-11 lg:pb-11 text-white relative"
    style="background-color: #020308"
  >
    <img
      src="../../assets/images/foreplay-logo.png"
      class="fixed top-4 md:top-8 left-4 md:left-9 h-10 w-auto cursor-pointer"
      style="z-index: 110"
      @click="() => { logoutUser() }"
    >

    <div class="w-full h-full flex items-center justify-center">
      <div class="bg-white rounded-2xl w-full max-w-md p-5">
        <!-- Loading State -->
        <div
          v-if="loading"
          class="relative w-full flex flex-col items-center justify-center gap-3"
          style="min-height: 220px;"
        >
          <BaseLoadingLogo class="relative top-2" />
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            Loading team invites...
          </BaseText>
        </div>

        <!-- List Mode: Multiple Invitations -->
        <div
          v-else-if="invitations.length && !selectedInvitation"
          class="relative w-full flex flex-col gap-4 fade-in"
        >
          <div class="w-full flex items-end justify-between">
            <BaseText
              type="label"
              class="text-text-muted text-left"
            >
              Your Pending Team Invitations
            </BaseText>
            <div class="text-text-subdued text-right">
              <BaseText
                size="xs"
                class="text-text-subdued"
              >
                {{ invitations.length }} invitations
              </BaseText>
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <div
              v-for="item in invitations"
              :key="item.invitation.id"
              class="invitation-row w-full flex items-center gap-3 p-3 rounded-xl border border-border-normal hover:shadow-md transition-shadow cursor-pointer"
              @click="selectInvitation(item)"
            >
              <!-- Team Avatar -->
              <img
                v-if="item.team.avatar"
                :src="item.team.avatar"
                alt="Team Avatar"
                class="w-10 h-10 rounded-xl flex-shrink-0"
              >
              <div
                v-else
                class="w-10 h-10 rounded-lg flex-shrink-0 bg-neutral-900 flex items-center justify-center"
              >
                <BaseText
                  type="label"
                  size="sm"
                  class="text-white"
                >
                  {{ item.team.teamName?.charAt(0)?.toUpperCase() || 'T' }}
                </BaseText>
              </div>

              <!-- Team Name -->
              <div class="flex-1">
                <BaseText
                  type="label"
                  size="md"
                  class="flex-grow text-text-muted"
                >
                  {{ item.team.teamName }}
                </BaseText>
                <BaseText
                  v-if="item.invitation?.createdAt"
                  size="xs"
                  class="text-text-subdued"
                >
                  Created {{ formatCreatedDaysAgo(item.invitation.createdAt) }}
                </BaseText>
              </div>

              <ChevronIcon class="text-text-muted transform -rotate-90" />
            </div>
          </div>
          <BaseButton
            primary
            class="w-full"
            @click="goBackToOnboardView"
          >
            <BaseText
              type="label"
              size="sm"
            >
              Return to onboarding
            </BaseText>
          </BaseButton>
        </div>

        <div v-else-if="!selectedInvitation && invitations.length === 0">
          <div class="flex flex-col items-center justify-between text-center gap-6 fade-in">
            <div class="flex items-center justify-center bg-gray-100 rounded-full p-2 w-12 h-12">
              <EmailIcon class="w-full h-full text-gray-400" />
            </div>

            <div>
              <BaseText
                type="label"
                size="lg"
                class="text-text-muted"
              >
                No invitations found
              </BaseText>
              <BaseText
                size="sm"
                class="text-text-subdued max-w-xs"
              >
                Ask your admin to send an invitation to
                <span
                  v-if="getUser?.email"
                  class="text-text-muted"
                >{{ getUser?.email }}</span>
                <span v-else>your email</span>
              </BaseText>
            </div>
            <div class="space-y-2 w-full mt-2">
              <BaseButton
                primary
                class="w-full"
                @click="goBackToOnboardView"
              >
                <BaseText
                  type="label"
                  size="sm"
                >
                  Go back
                </BaseText>
              </BaseButton>
              <button
                class="text-text-subdued underline cursor-pointer hover:text-text-muted transition-colors"
                @click="getAllInvitations"
              >
                <BaseLoadingSpinnerCircle
                  v-if="loading"
                  class="w-4 h-4 text-text-muted"
                />
                <BaseText
                  v-else
                  size="xs"
                  class="text-text-subdued"
                >
                  Refresh
                </BaseText>
              </button>
            </div>
          </div>
        </div>

        <!-- Detail View: Selected from list OR direct link -->
        <div
          v-else-if="selectedInvitation || teamInviteId"
          class="relative w-full flex flex-col gap-4 fade-in"
        >
          <!-- Back Button (only in list mode) -->
          <button
            v-if="selectedInvitation"
            class="flex items-center gap-1 text-text-muted transition-colors self-start -ml-1 hover:bg-background-hover rounded-md px-1.5 py-0.5"
            @click="goBackToList"
          >
            <ChevronIcon class="transform rotate-90 text-text-muted" />
            <BaseText
              type="label"
              size="sm"
            >
              Go back
            </BaseText>
          </button>

          <!-- Team Logo and Owner Avatar -->
          <div
            class="relative mx-auto"
            style="width: 68px; height: 68px;"
          >
            <!-- Team Logo -->
            <img
              v-if="team.avatar"
              :src="team.avatar"
              class="team-logo w-9 h-9 rounded-md"
              alt="Team Logo"
            >
            <div
              v-else
              class="team-logo w-9 h-9 flex items-center justify-center p-2 rounded-md bg-neutral-900"
            >
              <BaseText
                type="label"
                size="sm"
                class="text-white uppercase"
              >
                {{ team?.teamName?.charAt(0) || 'T' }}
              </BaseText>
            </div>
            <!-- Team Owner Avatar -->
            <img
              v-if="teamOwner.avatar"
              :src="teamOwner.avatar"
              class="owner-avatar w-9 h-9 rounded-full"
              alt="Team Owner Avatar"
              referrerpolicy="no-referrer"
            >
            <div
              v-else
              class="owner-avatar w-9 h-9 flex items-center justify-center p-2 rounded-full bg-neutral-50"
            >
              <BaseText
                type="label"
                size="sm"
                class="text-text-muted"
              >
                {{ teamOwner?.name?.charAt(0) || teamOwner?.first_name?.charAt(0) || teamOwner?.last_name?.charAt(0) || teamOwner?.email?.charAt(0) || 'U' }}
              </BaseText>
            </div>
          </div>

          <!-- Invite Header -->
          <BaseText
            type="label"
            size="md"
            class="text-text-muted text-center"
          >
            You're invited to join {{ team.teamName ? `'${team.teamName}'` : 'a team' }}
          </BaseText>

          <!-- Invite Message -->
          <BaseText
            type="body"
            size="sm"
            class="text-text-normal text-center px-4"
          >
            {{ getInviterName }} has invited you to join their team and collaborate on exciting projects together.
          </BaseText>

          <!-- Action Buttons -->
          <div class="w-full flex justify-between mt-2">
            <button
              class="flex justify-center px-2 py-1.5 rounded-md transition duration-100"
              :class="loadingDecline ? 'bg-neutral-25 cursor-default' : 'hover:bg-neutral-25'"
              style="width: 66px;"
              :disabled="loadingDecline || loadingAccept"
              @click="rejectInvitation"
            >
              <BaseLoadingSpinnerCircle
                v-if="loadingDecline"
                class="text-text-disabled"
              />
              <BaseText
                v-else
                type="label"
                size="sm"
                class="text-text-muted"
              >
                Decline
              </BaseText>
            </button>
            <button
              class="accept-btn py-1.5 px-6 rounded-md"
              :disabled="loadingAccept || loadingDecline"
              @click="acceptInvitation"
            >
              <BaseLoadingSpinnerCircle
                v-if="loadingAccept"
                class="text-white"
              />
              <BaseText
                v-else
                type="label"
                size="sm"
                class="text-white"
              >
                Accept
              </BaseText>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import FirebaseAPI from '@/api/firebase'
import ForeplayAPI from '@/api/foreplayServer'
import CloudFunctionsAPI from '@/api'
import moment from 'moment'

import ChevronIcon from '@/components/globals/Icons/ChevronIcon.vue'
import EmailIcon from '@/components/globals/Icons/EmailIcon.vue'

export default {
  name: 'TeamInviteView',
  components: {
    ChevronIcon,
    EmailIcon
  },
  data () {
    return {
      team: {},
      invitation: {},
      teamOwner: {},

      teamInviteId: null,

      loading: true,
      loadingAccept: false,
      loadingDecline: false,

      // List mode
      invitations: [],
      selectedInvitation: null
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getTeam', 'getUser']),
    getInviterName () {
      if (this.teamOwner.name) {
        return this.teamOwner.name
      }
      if (this.teamOwner.first_name) {
        return this.teamOwner.last_name
          ? `${this.teamOwner.first_name} ${this.teamOwner.last_name}`
          : this.teamOwner.first_name
      }
      if (this.teamOwner.email) {
        return this.teamOwner.email
      }
      return 'user'
    }
  },
  async mounted () {
    this.teamInviteId = this.$route.query.id
    if (this.teamInviteId) {
      await this.getTeamInvite(this.teamInviteId)
    }
    await this.getAllInvitations()
  },
  methods: {
    ...mapActions('AuthModule', [
      'logoutUser',
      'fetchUserTeam',
      'fetchUserSubscription',
      'fetchAppLimits',
      'fetchUserInvitations'
    ]),
    ...mapActions('BoardsModule', ['fetchBoards', 'fetchFolders']),
    ...mapActions('SpyderModule', ['fetchSpyderSubscriptions']),
    ...mapActions('BriefsModule', ['fetchBriefs']),
    ...mapMutations('AuthModule', ['SET_USER']),
    resolveTeamOwner (invitation = {}, fallbackOwner = {}) {
      if (invitation?.createdByUser && typeof invitation.createdByUser === 'object') {
        return invitation.createdByUser
      }
      if (fallbackOwner && typeof fallbackOwner === 'object') {
        return fallbackOwner
      }
      return {}
    },
    handleStaleInvitationState () {
      if (this.selectedInvitation && this.invitation?.id) {
        this.invitations = this.invitations.filter(
          item => item.invitation.id !== this.invitation.id
        )
        if (this.invitations.length > 0) {
          this.goBackToList()
          return
        }
      }

      this.$router.push({ name: 'UserOnboardView' })
    },
    formatCreatedDaysAgo (createdAt) {
      return moment(createdAt).fromNow()
    },
    async getTeamInvite (teamInviteId) {
      try {
        const response = await FirebaseAPI.Invitations.getById(teamInviteId)
        this.team = response.team
        this.invitation = response.invitation
        this.teamOwner = this.resolveTeamOwner(response.invitation, response.createdBy)
        this.selectedInvitation = response
      } catch (error) {
        console.error(error)
        const errMessage = error.response?.data?.message || 'Error fetching team invite'
        this.$showAlert({
          message: errMessage,
          type: 'error'
        })
        this.$router.push({ name: 'UserOnboardView' })
      } finally {
        this.loading = false
      }
    },
    async getAllInvitations () {
      this.loading = true
      try {
        const invitations = await FirebaseAPI.Invitations.getAllPending()

        if (!invitations.length) {
          this.$showAlert({
            message: 'No pending invitations',
            type: 'info'
          })
          return
        }

        // Fetch team data for each invitation without failing the whole list.
        const invitationResults = await Promise.allSettled(
          invitations.map(async (inv) => {
            const { team, ...invitation } = inv
            return {
              invitation,
              team,
              teamOwner: this.resolveTeamOwner(inv)
            }
          })
        )

        const invitationsWithTeams = invitationResults
          .filter(result => result.status === 'fulfilled')
          .map(result => result.value)

        const failedInvitations = invitationResults.length - invitationsWithTeams.length
        if (failedInvitations > 0) {
          this.$showAlert({
            type: 'warning',
            message: `Could not load ${failedInvitations} invitation${failedInvitations > 1 ? 's' : ''}.`
          })
        }

        this.invitations = invitationsWithTeams
      } catch (error) {
        console.error(error)
        this.$showAlert({
          message: 'Error fetching invitations',
          type: 'error'
        })
        this.$router.push({ name: 'UserOnboardView' })
      } finally {
        this.loading = false
      }
    },
    selectInvitation (item) {
      this.selectedInvitation = item
      this.team = item.team
      this.invitation = item.invitation
      this.teamOwner = this.resolveTeamOwner(item.invitation, item.teamOwner)
    },
    goBackToList () {
      this.selectedInvitation = null
      this.team = {}
      this.invitation = {}
      this.teamOwner = {}
    },
    goBackToOnboardView () {
      this.$router.push({ name: 'UserOnboardView' })
    },
    async acceptInvitation () {
      this.loadingAccept = true
      try {
        if (!this.invitation.id || !this.invitation.teamId) {
          this.handleStaleInvitationState()
          return
        }
        await ForeplayAPI.Teams.respondToInvitation(this.invitation.id, 'accepted')
        await CloudFunctionsAPI.Teams.migrateUserToTeam(this.invitation.teamId)

        this.$showAlert({
          type: 'success',
          message: 'Team joined successfully!'
        })

        // Handle list mode
        if (this.selectedInvitation) {
          this.invitations = this.invitations.filter(
            item => item.invitation.id !== this.invitation.id
          )
          if (this.invitations.length > 0) {
            this.goBackToList()
          }
        }

        // joined the team, skip onboarding and go to discovery

        try {
          await FirebaseAPI.Users.update(this.getUser.user_id, { onboardingStatus: 'FINISH' })

          const updatedUser = { ...this.getUser }
          updatedUser.onboardingStatus = 'FINISH'
          this.SET_USER(updatedUser)

          // Refresh team + subscription before limits (limits use team.created_by + subscription)
          await this.fetchUserTeam()
          await this.fetchUserSubscription(true)
          await this.fetchAppLimits()

          await Promise.all([
            this.fetchBoards(),
            this.fetchFolders(),
            this.fetchSpyderSubscriptions(),
            this.fetchBriefs(),
            this.fetchUserInvitations()
          ])

          this.$router.push({ name: 'DiscoveryView' })
        } catch (error) {
          console.error(error)
          this.$showAlert({ type: 'error', message: 'Error redirecting. Please refresh your browser' })
        }
      } catch (error) {
        console.error(error)
        this.$showAlert({
          message: error.message || 'Error accepting invitation',
          type: 'error'
        })
      } finally {
        this.loadingAccept = false
      }
    },
    async rejectInvitation () {
      this.loadingDecline = true
      try {
        if (!this.invitation?.id) {
          this.handleStaleInvitationState()
          return
        }

        await ForeplayAPI.Teams.respondToInvitation(this.invitation.id, 'rejected')

        this.$showAlert({
          type: 'info',
          message: 'Invitation declined'
        })

        // Handle list mode
        if (this.selectedInvitation) {
          this.invitations = this.invitations.filter(
            item => item.invitation.id !== this.invitation.id
          )
          if (this.invitations.length > 0) {
            this.goBackToList()
          } else {
            this.$router.push({ name: 'UserOnboardView' })
          }
        } else {
          this.$router.push({ name: 'UserOnboardView' })
        }
      } catch (error) {
        console.error(error)
        this.$showAlert({
          message: error.message || 'Error declining invitation',
          type: 'error'
        })
      } finally {
        this.loadingDecline = false
      }
    }
  }
}
</script>

<style scoped>
.team-logo {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}
.owner-avatar {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
}

@property --accept-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --accept-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --accept-color {
  syntax: '<color>';
  initial-value: #0063F4;
  inherits: false;
}
.accept-btn {
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--accept-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--accept-btn-opacity-2)) 100%), var(--accept-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px #0063F4;
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out,
    background-color 100ms ease-in-out,
    --accept-btn-opacity-1 100ms ease-in-out,
    --accept-btn-opacity-2 100ms ease-in-out,
    --accept-color 100ms ease-in-out;
}
.accept-btn:hover {
  --accept-btn-opacity-1: 0.24;
  --accept-btn-opacity-2: 0;
}
.accept-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
